import { ReactNode } from 'react';
import Button from '@/components/Button';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import Link from 'next/link';
import Image from 'next/image';
import clsx from 'clsx';
import { type DemoLayoutType, DemoLayout } from '@/sections/product-tour/contexts/demoLayoutContext';
import { noop } from '@/lib/constants';
import { StaticImageData } from 'next/image';

interface DemoCardProps {
  title: string;
  desc: string;
  link: string;
  layoutType: DemoLayoutType;
  cardImages: {
    row: StaticImageData;
    grid: StaticImageData;
  };
  Icon: ReactNode;
}

export function DemoCard({ title, desc, link, layoutType, cardImages, Icon }: DemoCardProps) {
  return (
    <Link
      id={title}
      href={link}
      className={clsx(
        'demo-card-gsap group flex w-full scroll-m-64 flex-col gap-6 rounded-2xl bg-white shadow-md transition-shadow hover:shadow-lg hover:outline hover:outline-2 hover:outline-gradle-green',
        layoutType == DemoLayout.GRID ? '' : 'md:flex-row md:p-8',
      )}
    >
      <div
        className={clsx(
          'relative h-40 max-h-52 overflow-hidden rounded-t-2xl lg:h-48',
          layoutType == DemoLayout.GRID ? '' : 'md:min-h-72 md:flex-1 md:rounded-2xl lg:min-h-[21rem]',
        )}
      >
        <div className="absolute left-3 top-3 z-10 flex size-10 items-center justify-center rounded-lg bg-gradle-blue bg-gradient-to-br from-primary to-secondary">
          <div className="size-6 text-white">{Icon}</div>
        </div>
        <Image
          alt="demo preview image"
          src={layoutType == DemoLayout.GRID ? cardImages.grid : cardImages.row}
          placeholder="blur"
          fill
          className={clsx(
            'rounded-t-2xl bg-cover object-cover transition-transform ease-out group-hover:scale-110',
            layoutType == DemoLayout.GRID ? '' : 'md:rounded-2xl',
          )}
        />
      </div>
      <div
        className={clsx(
          'flex flex-col gap-6 px-4 pb-6',
          layoutType == DemoLayout.GRID ? 'lg:px-6' : 'md:flex-1 md:justify-between md:px-0 md:pb-0 md:pt-4 lg:px-0',
        )}
      >
        <div className={clsx('flex flex-col gap-2', layoutType == DemoLayout.GRID ? '' : 'md:gap-4')}>
          <h4 className="text-eyebrow-strong">{title}</h4>
          <p
            className={clsx(
              'text-body line-clamp-3 min-h-11 overflow-y-hidden overflow-ellipsis text-gray-3',
              layoutType == DemoLayout.GRID
                ? 'md:min-h-12 lg:min-h-[3.125rem] xl:min-h-14'
                : 'min-h-[none] md:line-clamp-4',
            )}
          >
            {desc}
          </p>
        </div>
        <div className="flex items-center justify-center">
          <span className="text-body mr-auto text-gray-1 transition-colors group-hover:text-gradle-blue">
            Learn more{' '}
          </span>
          <Button
            outline
            variant="ghost-light"
            size="xs"
            className="self-end from-primary to-secondary outline outline-1 outline-gray-5 transition-[background-image_translate] duration-300 group-hover:-translate-y-0.5 group-hover:bg-gradient-to-br group-hover:outline-none"
            roundedFull
            onClick={noop}
          >
            <ArrowRightIcon className="size-6 text-black group-hover:text-white" />
          </Button>
        </div>
      </div>
    </Link>
  );
}
